import React from "react";
import axios from "axios";
import moment from "moment";
import { Dropdown, Menu, Modal, Popconfirm, Spin, message } from "antd";
import { MdContentCopy, MdDeleteOutline, MdOutlineRemoveRedEye } from "react-icons/md";
import { useMutation, useQuery, useQueryClient } from "react-query";

import ContentBlock from "../../../../components/Layout/ContentBlock";
import FilterMenu from "../../../../components/Layout/FilterMenu";
import {
  ButtonSecondary,
  InputSwitchComponent,
  Pagination,
  TextInput,
} from "../../../../components/globals";
import TableFilterBox from "../../../../components/common/TableFilterBox";
import { ButtonEditRow, SelectForm } from "../../../../components/common/Buttons";
import RangePickerComp from "../../../../components/common/RangePickerComp";
import { DEFAULT_PER_PAGE } from "../../../../helpers/default";
import { prettyDateFormat } from "../../../../helpers/Utils";
import { useBulkSelection, useModal } from "../../../../hooks";
import {
  BulkActionBar,
  BulkHeaderCheckbox,
  BulkRowCheckbox,
} from "../../../../components/common/BulkSelection";
import ModalBlock from "../modals/ModalBlock";
import InfoZoneIcon from "./InfoZoneIcon";
import InfoZoneManagerTabUi from "./InfoZoneManagerTabUi";
import { BLOCK_TYPE_OPTIONS, blockTypeLabel } from "./blockTypes";
import {
  PREVIEW_SANDBOX,
  previewSrc,
  renderBlockPreview,
} from "./blockPreview";
import { showApiError } from "./apiErrors";

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Enabled", value: "true" },
  { label: "Disabled", value: "false" },
];

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  ...BLOCK_TYPE_OPTIONS,
];

const buildBlockQuery = ({ page, perPage, filters }) => {
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("per_page", perPage);

  if (filters.title) {
    params.append("q[title_or_local_name_i_cont]", filters.title);
  }
  if (filters.blockType) {
    params.append("q[block_type_eq]", filters.blockType);
  }
  if (filters.infoZone) {
    params.append("q[banners_id_eq]", filters.infoZone);
  }
  if (Array.isArray(filters.datePeriod) && filters.datePeriod.length === 2) {
    params.append("q[valid_to_gteq]", filters.datePeriod[0]);
    params.append("q[valid_from_lteq]", filters.datePeriod[1]);
  }
  if (filters.status) {
    params.append("q[block_status_eq]", filters.status);
  }

  return params.toString();
};

const formatPeriod = (validFrom, validTo) => {
  const fromLabel = validFrom ? prettyDateFormat(validFrom) : "No start date";
  const toLabel = validTo ? prettyDateFormat(validTo) : "No end date";
  return `${fromLabel} - ${toLabel}`;
};

const getDateFormat = (date) => {
  const normalizedDate = moment(date);
  return date && normalizedDate.isValid() ? normalizedDate : "";
};

const DEFAULT_FILTERS = {
  title: "",
  blockType: "",
  infoZone: "",
  datePeriod: [],
  status: "",
};

const INITIAL_PREVIEW_HEIGHT = 320;
const FALLBACK_PREVIEW_HEIGHT = 1600;
const PREVIEW_HEIGHT_TIMEOUT = 1500;

function BlocksTab({ tabId, setTabId }) {
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(DEFAULT_PER_PAGE);
  const [filterTitle, setFilterTitle] = React.useState("");
  const [filterBlockType, setFilterBlockType] = React.useState("");
  const [filterInfoZone, setFilterInfoZone] = React.useState("");
  const [filterDatePeriod, setFilterDatePeriod] = React.useState([]);
  const [filterStatus, setFilterStatus] = React.useState("");
  const [appliedFilters, setAppliedFilters] = React.useState(DEFAULT_FILTERS);
  const { modalOpt, setModalOpt, closeModal } = useModal();
  const [previewId, setPreviewId] = React.useState(null);
  const [previewHeight, setPreviewHeight] = React.useState(
    INITIAL_PREVIEW_HEIGHT
  );
  const previewFrame = React.useRef(null);
  const previewReported = React.useRef(false);

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (
        !previewFrame.current ||
        event.source !== previewFrame.current.contentWindow ||
        event.data?.type !== "block-preview-height"
      ) {
        return;
      }

      const height = Number(event.data.height);
      if (!height) return;

      previewReported.current = true;
      setPreviewHeight(Math.min(Math.max(height, 120), 4000));
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const openPreview = (id) => {
    previewReported.current = false;
    setPreviewHeight(INITIAL_PREVIEW_HEIGHT);
    setPreviewId(id);
  };

  React.useEffect(() => {
    if (!previewId) return undefined;

    const timer = setTimeout(() => {
      if (!previewReported.current) setPreviewHeight(FALLBACK_PREVIEW_HEIGHT);
    }, PREVIEW_HEIGHT_TIMEOUT);

    return () => clearTimeout(timer);
  }, [previewId]);

  const { data: previewBlock, isFetching: isLoadingPreview } = useQuery(
    ["info-zone-block-preview", previewId],
    async () => {
      const response = await axios.get(`/api/v1/banner_blocks/${previewId}`);
      return response.data;
    },
    {
      enabled: !!previewId,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  const { data: infoZoneOptions = [] } = useQuery(
    "info-zone-block-zone-options",
    async () => {
      const response = await axios.get("/api/v1/banners/info_zone_options");
      return (response.data?.data || []).map((banner) => ({
        value: String(banner.id),
        label: banner.title || `Info zone #${banner.id}`,
      }));
    }
  );

  const { data: blocks, isLoading } = useQuery(
    ["info-zone-blocks", page, perPage, appliedFilters],
    async () => {
      const query = buildBlockQuery({ page, perPage, filters: appliedFilters });
      const response = await axios.get(`/api/v1/banner_blocks?${query}`);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

  const duplicateMutation = useMutation({
    mutationFn: (id) => axios.post(`/api/v1/banner_blocks/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries("info-zone-blocks");
      queryClient.invalidateQueries("info-zone-banners");
      message.success("Block duplicated");
    },
    onError: (error) => {
      showApiError(error, "Failed to duplicate block");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/v1/banner_blocks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries("info-zone-blocks");
      queryClient.invalidateQueries("info-zone-banners");
      message.success("Block deleted");
    },
    onError: (error) => {
      showApiError(error, "Failed to delete block");
    },
  });

  const rows = blocks?.data || [];
  const totals = blocks?.totals || 0;
  const selection = useBulkSelection(totals);

  const bulkDeleteMutation = useMutation({
    mutationFn: (payload) => {
      const query = buildBlockQuery({ page, perPage, filters: appliedFilters });
      return axios.post(`/api/v1/banner_blocks/bulk_destroy?${query}`, payload);
    },
    onSuccess: () => {
      selection.clear();
      queryClient.invalidateQueries("info-zone-blocks");
      queryClient.invalidateQueries("info-zone-banners");
      message.success("Blocks deleted");
    },
    onError: (error) => {
      showApiError(error, "Failed to delete blocks");
    },
  });

  const applyFilters = () => {
    selection.clear();
    setAppliedFilters({
      title: filterTitle,
      blockType: filterBlockType,
      infoZone: filterInfoZone,
      datePeriod: filterDatePeriod,
      status: filterStatus,
    });
    setPage(1);
  };

  const handleFilterClear = () => {
    selection.clear();
    setFilterTitle("");
    setFilterBlockType("");
    setFilterInfoZone("");
    setFilterDatePeriod([]);
    setFilterStatus("");
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  return (
    <>
      <ContentBlock>
        <FilterMenu
          text2="Blocks"
          iconComponent={<InfoZoneIcon />}
          onChangeCountPage={setPerPage}
          countPage={perPage}
          page={page}
          totals={totals}
        />
      </ContentBlock>
      <div className="col-12">
        <InfoZoneManagerTabUi tabId={tabId} setTabId={setTabId} />
      </div>
      <ContentBlock containerClass="py-1">
        <TableFilterBox
          filter={true}
          onFilter={applyFilters}
          clearFilter={handleFilterClear}
          button1={
            <div className="float-left ml-2" style={{ width: 110 }}>
              <Dropdown
                trigger={["click"]}
                overlay={
                  <Menu
                    items={BLOCK_TYPE_OPTIONS.map((option) => ({
                      key: option.value,
                      label: option.label,
                    }))}
                    onClick={({ key }) =>
                      setModalOpt("new_block", 0, { block_type: key })
                    }
                  />
                }
              >
                <div>
                  <ButtonSecondary onClick={() => {}}>
                    New block
                  </ButtonSecondary>
                </div>
              </Dropdown>
            </div>
          }
        >
          <div className="d-flex justify-content-between flex-1 flex-wrap">
            <div className="d-flex flex-wrap">
              <div className="px-1">
                <TextInput
                  text="Title"
                  name="Title"
                  style={{ width: 165 }}
                  value={filterTitle}
                  onChange={(e) => setFilterTitle(e.target.value)}
                />
              </div>
              <div className="px-1" style={{ width: 165 }}>
                <SelectForm
                  placeholder="Block category"
                  options={TYPE_FILTER_OPTIONS}
                  selector="info_zone_block_type"
                  onlyPlaceholder="Block category"
                  value={filterBlockType}
                  onChange={(val) => setFilterBlockType(val || "")}
                />
              </div>
              <div className="px-1" style={{ width: 200 }}>
                <SelectForm
                  placeholder="Info zone"
                  options={infoZoneOptions}
                  selector="info_zone_block_zone"
                  onlyPlaceholder="Info zone"
                  value={filterInfoZone}
                  onChange={(val) => setFilterInfoZone(val || "")}
                />
              </div>
              <div className="px-1">
                <RangePickerComp
                  style={{ width: 250 }}
                  label="Date period"
                  placeholder={["Start date", "End date"]}
                  value={
                    filterDatePeriod?.length
                      ? [
                          getDateFormat(filterDatePeriod[0]),
                          getDateFormat(filterDatePeriod[1]),
                        ]
                      : ["", ""]
                  }
                  onChange={(_, value) => setFilterDatePeriod(value)}
                  disabledDate={() => false}
                />
              </div>
              <div className="px-1">
                <InputSwitchComponent
                  label="Status"
                  name="info_zone_block_status"
                  options={STATUS_OPTIONS}
                  value={filterStatus}
                  onChange={(val) => setFilterStatus(val.target.value)}
                />
              </div>
            </div>
          </div>
        </TableFilterBox>
        <BulkActionBar
          selection={selection}
          noun="blocks"
          confirmContent="They will be removed from every info zone they are assigned to. You will not be able to undo this action"
          loading={bulkDeleteMutation.isLoading}
          onConfirm={(payload) => bulkDeleteMutation.mutate(payload)}
        />

        <div className="table-responsive" style={{ overflow: "hidden" }}>
          <table className="table mt-3">
            <thead>
              <tr className="text-muted">
                <th style={{ width: 36 }}>
                  <BulkHeaderCheckbox selection={selection} disabled={!totals} />
                </th>
                <th className="text-black">Title</th>
                <th className="text-black">Block category</th>
                <th className="text-black">Valide period</th>
                <th className="text-black">Background color</th>
                <th className="text-black">Items</th>
                <th className="text-black">Info zones</th>
                <th className="text-black"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr className="align-middle tableColumn" key={item.id}>
                  <td className="align-middle">
                    <BulkRowCheckbox selection={selection} id={item.id} />
                  </td>
                  <td className="align-middle">
                    <div>
                      {item.title ||
                        item.local_name ||
                        item.system_name ||
                        `Block #${item.id}`}
                    </div>
                    {!!item.title && !!item.local_name && (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {item.local_name}
                      </div>
                    )}
                  </td>
                  <td className="align-middle">
                    <div
                      style={{
                        background: "#F2F2F2",
                        color: "#454545",
                        borderRadius: 4,
                        padding: "4px 10px",
                        fontSize: 13,
                        display: "inline-block",
                        width: "fit-content",
                      }}
                    >
                      {item.type_label || blockTypeLabel(item.block_type)}
                    </div>
                  </td>
                  <td className="align-middle">
                    <div className="d-flex flex-row align-items-center">
                      <div
                        className="dot"
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          marginRight: 8,
                          backgroundColor: item.active_now ? "#28A745" : "#C94747",
                        }}
                      />
                      {formatPeriod(item.valid_from, item.valid_to)}
                    </div>
                  </td>
                  <td className="align-middle">
                    {item.background_color || "Default"}
                  </td>
                  <td className="align-middle">{item.items_count}</td>
                  <td className="align-middle">
                    <div className="d-flex flex-column" style={{ gap: 4 }}>
                      {(item.info_zones || []).map((infoZone) => (
                        <div key={`${item.id}-zone-${infoZone.id}`}>
                          {infoZone.title || `Info zone #${infoZone.id}`}
                        </div>
                      ))}
                      {!item.info_zones?.length && (
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          Not assigned
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="align-middle">
                    <div
                      className="d-flex justify-content-end align-items-center"
                      style={{ gap: 8 }}
                    >
                      <button
                        type="button"
                        className="btn btn-sm d-flex justify-content-center align-items-center"
                        style={{
                          width: 28,
                          height: 28,
                          padding: 0,
                          background: "#FFA940",
                        }}
                        title="Preview block"
                        onClick={() => openPreview(item.id)}
                      >
                        <MdOutlineRemoveRedEye size={16} color="#fff" />
                      </button>
                      <ButtonEditRow
                        className="d-flex justify-content-center align-items-center"
                        onClick={() => setModalOpt("edit_block", item.id, item)}
                      />
                      <Popconfirm
                        title="Duplicate this block?"
                        description="A copy with the same banners will be added to the info zones this block is assigned to."
                        okText="Duplicate"
                        cancelText="Cancel"
                        onConfirm={() => duplicateMutation.mutate(item.id)}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-primary d-flex justify-content-center align-items-center"
                          style={{ width: 28, height: 28, padding: 0 }}
                        >
                          <MdContentCopy size={14} color="#fff" />
                        </button>
                      </Popconfirm>
                      <Popconfirm
                        title="Delete this block?"
                        description="It will be removed from every info zone it is assigned to."
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteMutation.mutate(item.id)}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-danger d-flex justify-content-center align-items-center"
                          style={{ width: 28, height: 28, padding: 0 }}
                        >
                          <MdDeleteOutline size={16} color="#fff" />
                        </button>
                      </Popconfirm>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && !isLoading && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No blocks found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <Pagination
            setPagination={setPage}
            page={page}
            totals={totals}
            pageCount={perPage}
          />
        </div>
      </ContentBlock>

      <Modal
        title={
          previewBlock
            ? `${previewBlock.title || "Block"} - ${
                previewBlock.type_label || blockTypeLabel(previewBlock.block_type)
              }`
            : "Preview"
        }
        visible={!!previewId}
        footer={null}
        width={1024}
        onCancel={() => setPreviewId(null)}
        bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
      >
        {isLoadingPreview || !previewBlock ? (
          <div className="text-center py-4">
            <Spin />
          </div>
        ) : (
          <iframe
            title="Block preview"
            src={previewSrc(renderBlockPreview(previewBlock))}
            ref={previewFrame}
            sandbox={PREVIEW_SANDBOX}
            scrolling="auto"
            style={{
              width: "100%",
              height: previewHeight,
              border: "1px solid #EFEFEF",
              borderRadius: 4,
            }}
          />
        )}
      </Modal>

      {(modalOpt.type === "new_block" || modalOpt.type === "edit_block") && (
        <ModalBlock onClose={closeModal} modalOpt={modalOpt} />
      )}
    </>
  );
}

export default BlocksTab;
